import React, { useRef } from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import Textarea from "@mui/joy/Textarea";
import { IconButton, Stack } from "@mui/joy";
import useScript from "../../utils/useScript";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { MessageTypes } from "../../utils/chat-types";
import useSocketStore from "../../stores/store-socket";

const useEnhancedEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

/**
 * Props interface for the MessageInput component.
 * 
 * @interface MessageInputProps
 * @property {string} textAreaValue - The current value of the text area
 * @property {(value: string) => void} setTextAreaValue - Function to update the text area value
 * @property {() => void} onTextSubmit - Function to handle text submission
 * @property {(fileData: any) => void} onFileSubmitCallback - Function to handle file submission
 */
export type MessageInputProps = {
  textAreaValue: string;
  setTextAreaValue: (value: string) => void;
  onTextSubmit: () => void;
  onFileSubmitCallback: (fileData: any) => void;
};

/**
 * A component that provides a text input area for sending messages and files.
 * Supports typing indicators and keyboard shortcuts for sending messages.
 * 
 * @component
 * @param {MessageInputProps} props - The props for the MessageInput component
 * @returns {JSX.Element} A text input area with file attachment capabilities
 * 
 * @example
 * ```tsx
 * <MessageInput
 *   textAreaValue={message}
 *   setTextAreaValue={setMessage}
 *   onTextSubmit={handleSubmit}
 *   onFileSubmitCallback={handleFileSubmit}
 * />
 * ```
 */
export default function MessageInput({
  textAreaValue,
  setTextAreaValue,
  onTextSubmit,
  onFileSubmitCallback,
}: MessageInputProps) {
  const inputFile = useRef<HTMLInputElement | null>(null);
  const { sendNewMessage, selectedChat } = useSocketStore();

  /**
   * Handles file submission by notifying the parent component.
   * 
   * @param {any} file - The file to be submitted
   */
  const onFileSubmit = async (file: any) => {
    if (file) {
      try {
        // Notify the parent component about the uploaded file
        onFileSubmitCallback(file); // Call the parent callback function
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    }
  };

  /**
   * Triggers the file input click event.
   */
  const onAtachClick = () => {
    if (inputFile.current) {
      inputFile.current.click();
    }
  };

  /**
   * Handles file input change events.
   * 
   * @param {React.ChangeEvent<HTMLInputElement>} event - The file input change event
   */
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      onFileSubmit(event.target.files[0]);
      // Clear the file input value by its id
      const fileInput = document.getElementById(
        "fileInput"
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    }
  };

  const status = useScript(`https://unpkg.com/feather-icons`);
  const textAreaRef = React.useRef<HTMLDivElement>(null);

  /**
   * Handles the click event for sending a message.
   */
  const handleClick = () => {
    if (textAreaValue.trim() !== "") {
      onTextSubmit();
      setTextAreaValue("");
    }
  };

  /**
   * Handles keyboard events in the text area.
   * Supports Enter to send and Shift+Enter for new lines.
   * 
   * @param {React.KeyboardEvent<HTMLTextAreaElement>} event - The keyboard event
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        // Insert a new line when Shift + Enter is pressed
        event.preventDefault();
        const { selectionStart, selectionEnd } = event.currentTarget;
        const value = textAreaValue;
        setTextAreaValue(
          value.substring(0, selectionStart) +
            "\n" +
            value.substring(selectionEnd)
        );
      } else {
        // Send the message when Enter is pressed
        event.preventDefault();
        handleClick();
      }
    }
  };

  /**
   * Sends a typing indicator event to the socket.
   */
  const handleTyping = () => {
    // Send a typing event to the socket
    if (
      selectedChat &&
      selectedChat.type == "dialog" &&
      selectedChat.sender.online
    ) {
      const event = {
        msg_type: MessageTypes.IsTyping,
        dialog_id: selectedChat.sender.id,
      };
      sendNewMessage(event);
    }
  };

  /**
   * Sends a typing stopped event to the socket.
   */
  const handleTypingStopped = () => {
    // Send a typing stopped event to the socket
    if (
      selectedChat &&
      selectedChat.type == "dialog" &&
      selectedChat.sender.online
    ) {
      const event = {
        msg_type: MessageTypes.TypingStopped,
        dialog_id: selectedChat.sender.id,
      };
      sendNewMessage(event);
    }
  };

  useEnhancedEffect(() => {
    // Feather icon setup: https://github.com/feathericons/feather#4-replace
    // @ts-ignore
    if (typeof feather !== "undefined") {
      // @ts-ignore
      feather.replace();
    }
  }, [status]);
  return (
    <Box sx={{ px: 2, pb: 3 }}>
      <FormControl>
        <Textarea
          id="messageTextarea"
          placeholder="Geben Sie hier etwas ein ..."
          aria-label="Message"
          ref={textAreaRef}
          onChange={(e) => {
            setTextAreaValue(e.target.value);
            handleTyping();
          }}
          value={textAreaValue}
          minRows={2}
          maxRows={10}
          endDecorator={
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              flexGrow={1}
              minHeight={40}
            >
              <IconButton
                onClick={onAtachClick}
                variant="plain"
                color="neutral"
                size="md"
              >
                <AttachFileIcon />
              </IconButton>
              <input
                id="fileInput"
                type="file"
                ref={inputFile}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <Button onClick={handleClick}>Schicken</Button>
            </Stack>
          }
          onKeyDown={handleKeyDown}
          onKeyUp={handleTypingStopped}
        />
      </FormControl>
    </Box>
  );
}
